import * as React from 'react';
import { useLocation } from 'react-router';

import { ApolloError } from '@apollo/client';

import { GetCatalogProductViewQuery } from '@notino/shared/definitions/types';

import { Redirect } from '@components/HttpContext';
import PageNotFound, { PageLoadFailed } from '@components/NotFound';
import { Loading } from '@containers/ProductDetailContainer/loading';

import { ForbiddenProductScreen } from './ForbiddenProductScreen';
import { isVariantFromCatalogUrl, selectVariant } from './utils';

interface ProductStateHandlerProps {
  data: GetCatalogProductViewQuery | undefined;
  loading: boolean;
  error: ApolloError | undefined;
  isReviewPage?: boolean;
}

const addTrailingSlash = (url: string) => {
  return url.endsWith('/') ? url : `${url}/`;
};

export const ProductStateHandler: React.FC<
  React.PropsWithChildren<ProductStateHandlerProps>
> = ({ data, error, loading, children, isReviewPage }) => {
  const location = useLocation();

  if (!data?.productDetailByCatalogMasterId) {
    if (loading) {
      return <Loading />;
    }
    if (error) {
      return <PageLoadFailed reason="unable to load PD data" />;
    }

    return <PageNotFound reason="Product not found" />;
  }

  const { productDetailByCatalogMasterId } = data;
  let product: GetCatalogProductViewQuery['productDetailByCatalogMasterId'];
  try {
    // on ssr errors are not propagated (due to apollo bug) we need to check data
    product = productDetailByCatalogMasterId;
  } catch (e) {
    return <PageLoadFailed reason="unable to load PD data" />;
  }

  if (!product) {
    return <PageNotFound reason="Product not found" />;
  }

  const variant = selectVariant(product.variants, location.pathname);

  if (!variant) {
    return <Redirect to={`${product.url ?? ''}${location.search}`} />;
  }

  const pathName = addTrailingSlash(location.pathname);

  if (
    !isReviewPage &&
    !isVariantFromCatalogUrl(variant, pathName) &&
    product.url !== pathName &&
    variant.url !== pathName
  ) {
    return <Redirect to={`${variant.url ?? ''}${location.search}`} />;
  }

  if (product.isForbiddenToDisplay) {
    return <ForbiddenProductScreen product={product} />;
  }

  // eslint-disable-next-line react/jsx-no-useless-fragment
  return <>{children}</>;
};
